import { queryOptions } from "@tanstack/react-query";
import { getKeyOnlyQueryConfig } from "@/shared/lib/key-only-query-config";
import {
  fetchAnalysisVariables,
  fetchLinkedAnalyses,
  fetchLinkedAnalysisSteps,
  fetchVariables,
} from "./api";
import { groupVariablesByGroup } from "./mappers";
import type {
  NormalizedVariableFilterOptions,
  UiVariableGroup,
  Variable,
  VariableFilterOptions,
} from "./types";

function normalizeVariableFilterOptions(
  options: VariableFilterOptions = {},
): NormalizedVariableFilterOptions {
  return {
    parentVariableId: options.parentVariableId ?? null,
    includeGlobal: options.includeGlobal !== false,
  };
}

export const templatesKeys = {
  all: getKeyOnlyQueryConfig(["templates"]),
  variables: () => getKeyOnlyQueryConfig(["templates", "variables"]),
  analysisVariables: () =>
    getKeyOnlyQueryConfig(["templates", "analysisVariables"]),
  linkedAnalyses: () => getKeyOnlyQueryConfig(["templates", "linkedAnalyses"]),
  linkedAnalysisSteps: () =>
    getKeyOnlyQueryConfig(["templates", "linkedAnalysisSteps"]),
};

export const templateQueries = {
  all: templatesKeys.all,

  variables: (options?: VariableFilterOptions, currentTenantId?: string) => {
    const normalizedOptions = normalizeVariableFilterOptions(options);
    return queryOptions({
      queryKey: [
        ...templatesKeys.variables().queryKey,
        normalizedOptions,
        currentTenantId,
      ],
      queryFn: () =>
        fetchVariables(normalizedOptions, currentTenantId as string),
      enabled: Boolean(currentTenantId),
    });
  },

  rootVariables: (
    options?: Omit<VariableFilterOptions, "parentVariableId">,
    currentTenantId?: string,
  ) =>
    templateQueries.variables(
      { ...options, parentVariableId: null },
      currentTenantId,
    ),

  variableChildren: (
    parentVariableId: string,
    includeGlobal = true,
    currentTenantId?: string,
  ) =>
    queryOptions({
      queryKey: [
        ...templatesKeys.variables().queryKey,
        "children",
        parentVariableId,
        { includeGlobal },
        currentTenantId,
      ],
      queryFn: () =>
        fetchVariables(
          {
            parentVariableId,
            includeGlobal,
          },
          currentTenantId as string,
        ),
      enabled: Boolean(parentVariableId && currentTenantId),
    }),

  groupedRootVariables: (
    options?: Omit<VariableFilterOptions, "parentVariableId">,
    currentTenantId?: string,
  ) =>
    queryOptions({
      ...templateQueries.rootVariables(options, currentTenantId),
      select: groupVariablesByGroup,
    }),

  analysisVariables: (analysisId: string) =>
    queryOptions({
      queryKey: [...templatesKeys.analysisVariables().queryKey, analysisId],
      queryFn: () => fetchAnalysisVariables(analysisId),
      enabled: Boolean(analysisId),
    }),

  linkedAnalyses: () =>
    queryOptions({
      queryKey: templatesKeys.linkedAnalyses().queryKey,
      queryFn: fetchLinkedAnalyses,
    }),

  linkedAnalysisSteps: (analysisId: string) =>
    queryOptions({
      queryKey: [...templatesKeys.linkedAnalysisSteps().queryKey, analysisId],
      queryFn: () => fetchLinkedAnalysisSteps(analysisId),
      enabled: Boolean(analysisId),
    }),
};

export type { UiVariableGroup, Variable, VariableFilterOptions };
